import { Post } from 'layouts';
import { PostIndexProps } from 'layouts/Post';
import { GetStaticPaths, GetStaticProps } from 'next';
import { createApolloClient } from '@elevator/ui';
import { POST } from 'lib/graphql/queries/post';
import { Post as PostType } from 'types/post';
import { load } from 'cheerio';
import { encode } from 'url-encode-decode';
import { reportError } from 'lib/errors';
import { HOME_POSTS } from 'lib/graphql/queries/home';
import { HomePosts } from 'types/home';

/**
 * Searches content body HTML for at least one matching
 * blockquote with identifying class name.
 *
 * @param html - The HTML content to search
 * @param idClassName - The class name to search for
 * @returns True if a blockquote with the class exists
 */
const hasClass = (html: string, idClassName: string): boolean => {
  const $ = load(html);
  return $('blockquote').hasClass(idClassName);
};

/**
 * Post Page - Static Props
 *
 * Fetches post data at build time for static generation.
 * Uses ISR (Incremental Static Regeneration) with 3-minute revalidation.
 *
 * Features:
 * - Detects Instagram and Twitter embeds in post content
 * - Returns 404 if post doesn't exist
 * - Handles GraphQL errors gracefully
 *
 * @param params - Route parameters containing the post slug
 * @returns Props for the Post layout component or notFound flag
 */
export const getStaticProps: GetStaticProps<PostIndexProps> = async ({ params }) => {
  const client = createApolloClient(process.env.API_URL);

  let { slug } = params;
  slug = encode(slug as string);

  const props: PostIndexProps = {
    slug,
    hasInstagramEmbed: false,
    hasTwitterEmbed: false,
    isLoading: false,
  };

  try {
    const { data } = await client.query<{
      publishedPost: PostType;
    }>({
      query: POST,
      variables: {
        slug,
      },
    });

    props.isLoading = false;

    if (!data?.publishedPost) {
      // Post doesn't exist - trigger Next.js 404 page
      return {
        notFound: true,
      };
    }

    // Post exists - populate props
    props.post = data.publishedPost;
    props.hasInstagramEmbed = hasClass(data.publishedPost.content, 'instagram-media');
    props.hasTwitterEmbed = hasClass(data.publishedPost.content, 'twitter-tweet');
  } catch (err) {
    reportError(err, {
      metaData: {
        operation: 'graphql query publishedPost',
        slug,
        mode: 'getStaticProps',
      },
      severity: 'warning',
    });
    // On error, trigger 404
    return {
      notFound: true,
    };
  }

  return {
    props,
    revalidate: 180, // 3 minutes
  };
};

/**
 * Post Page - Static Paths
 *
 * Pre-generates paths for featured and published posts at build time.
 * Uses fallback: true to enable on-demand ISR for posts not in the initial list.
 *
 * @returns Pre-generated paths and fallback strategy
 */
export const getStaticPaths: GetStaticPaths<{ slug: string }> = async () => {
  try {
    const client = createApolloClient(process.env.API_URL);

    const { data } = await client.query<HomePosts>({
      query: HOME_POSTS,
      variables: {
        limit: 9,
        skip: 0,
      },
    });

    const posts = [...Object.values(data.featuredPosts), ...data.publishedPosts];

    const paths: Array<{ params: { slug: string } }> = posts
      .filter(({ slug }) => typeof slug === 'string')
      .map(({ slug }) => ({
        params: { slug },
      }));

    return { paths, fallback: true };
  } catch (err) {
    reportError(err, {
      metaData: {
        operation: 'graphql query homePosts',
        mode: 'getStaticPaths',
      },
      severity: 'warning',
    });

    // API unavailable at build time (e.g. local build without elevatorpitch-ts).
    // Pages are generated on first request via ISR fallback.
    return { paths: [], fallback: true };
  }
};

export default Post;
